import React, { useState, useMemo } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';

const ConflictListModal = ({
    isOpen,
    onClose,
    conflicts,
    currentWeekNumber,
    currentYear
}) => {
    const [showCurrentWeekOnly, setShowCurrentWeekOnly] = useState(false);

    // Group conflicts by schedule item and combine their reasons with week numbers
    const groupedConflicts = useMemo(() => {
        const groups = new Map();

        conflicts.forEach(conflict => {
            const key = `${conflict.itemId}-${conflict.day}-${conflict.time}-${conflict.room}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    ...conflict,
                    reasonGroups: new Map() // Map of base reason -> Set of weeks
                });
            }

            const group = groups.get(key);

            conflict.reasons.forEach(reason => {
                // Extract the base message without the week number
                const baseMessage = reason.reason.replace(/, week \d+/i, '').replace(/\(week \d+\)/i, '').trim();
                const weekMatch = reason.reason.match(/week (\d+)/i);
                const weekNumber = weekMatch ? parseInt(weekMatch[1], 10) : null;

                if (!group.reasonGroups.has(baseMessage)) {
                    group.reasonGroups.set(baseMessage, {
                        weeks: new Set(),
                        severity: reason.severity
                    });
                }

                if (weekNumber) {
                    group.reasonGroups.get(baseMessage).weeks.add(weekNumber);
                }
            });
        });

        // Convert back to array and format reasons
        return Array.from(groups.values()).map(group => {
            const formattedReasons = Array.from(group.reasonGroups.entries()).map(([baseMessage, data]) => {
                const weeks = Array.from(data.weeks).sort((a, b) => a - b);
                const weekText = weeks.length > 0 ? ` (weeks ${weeks.join(', ')})` : '';
                return {
                    reason: `${baseMessage}${weekText}`,
                    severity: data.severity
                };
            });

            return {
                ...group,
                reasons: formattedReasons,
                allWeeks: new Set(Array.from(group.reasonGroups.values()).flatMap(data => Array.from(data.weeks)))
            };
        });
    }, [conflicts]);

    // Filter conflicts based on the current week filter
    const filteredConflicts = useMemo(() => {
        if (!showCurrentWeekOnly) {
            return groupedConflicts;
        }

        return groupedConflicts.filter(conflict => {
            // Check if the current week is in any of the conflict's weeks
            return conflict.allWeeks.has(currentWeekNumber);
        });
    }, [groupedConflicts, showCurrentWeekOnly, currentWeekNumber]);

    // Get unique weeks from all conflicts for display
    const allWeeks = useMemo(() => {
        const weeks = new Set();
        groupedConflicts.forEach(conflict => {
            conflict.allWeeks.forEach(week => {
                weeks.add(week);
            });
        });
        return Array.from(weeks).sort((a, b) => a - b);
    }, [groupedConflicts]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Schedule Conflicts (${filteredConflicts.length}${showCurrentWeekOnly ? ` - Week ${currentWeekNumber}` : ''})`}
        >
            {/* Filter Controls */}
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={showCurrentWeekOnly}
                            onChange={(e) => setShowCurrentWeekOnly(e.target.checked)}
                            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                            Show current week only (Week {currentWeekNumber})
                        </span>
                    </label>
                </div>
            </div>

            {/* Conflict List */}
            {filteredConflicts.length > 0 ? (
                <ul className="space-y-3 max-h-[60vh] overflow-y-auto text-sm">
                    {filteredConflicts.map((conflict, index) => (
                        <li key={`${conflict.itemId}-${index}`} className="border-b pb-2 last:border-b-0">
                            <p className="font-semibold text-gray-800 capitalize">
                                {conflict.itemName} ({conflict.day} {conflict.time} @ {conflict.room})
                            </p>
                            <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                {conflict.reasons.map((reason, rIndex) => {
                                    // Highlight current week conflicts
                                    const isCurrentWeek = conflict.allWeeks.has(currentWeekNumber);
                                    const baseColorClass = reason.severity === 'red' ? 'text-red-600' : 'text-orange-600';
                                    const highlightClass = isCurrentWeek && !showCurrentWeekOnly ? 'font-medium bg-yellow-100 px-1 rounded' : '';

                                    return (
                                        <li
                                            key={rIndex}
                                            className={`${baseColorClass} ${highlightClass}`}
                                        >
                                            {reason.reason}
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-center py-8">
                    {showCurrentWeekOnly ? (
                        <div>
                            <p className="text-gray-500 mb-2">
                                No conflicts detected for week {currentWeekNumber}.
                            </p>
                            {groupedConflicts.length > 0 && (
                                <p className="text-sm text-gray-400">
                                    ({groupedConflicts.length} conflict{groupedConflicts.length !== 1 ? 's' : ''} in other weeks)
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-gray-500">No conflicts detected in the current schedule.</p>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-between items-center">
                <div className="text-xs text-gray-500">
                    {showCurrentWeekOnly ? (
                        `Showing ${filteredConflicts.length} of ${groupedConflicts.length} total conflicts`
                    ) : (
                        `Total conflicts: ${groupedConflicts.length}`
                    )}
                </div>
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};

export default ConflictListModal; 